import { FC } from 'react';
import {
  ActionsMenu,
  PageContentLayout,
  PageHeaderLayout,
  Loader,
  NothingFoundAlert,
  SideMenu,
} from 'melp-design/components';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { ROUTES } from 'config';
import { tabsSchema } from 'utils/schemas';
import { useTabsState } from 'utils/Tabs';
import { usePredefinedToasts } from 'utils/Toast';
import { useRouteParams } from 'utils/useIdParam';
import { useModalContext } from 'store/modal';
import {
  getSurveyPermissionName,
  useDeactivateSurvey,
  useDeleteSurvey,
  useSurvey,
} from 'store/surveys';
import { useConfirm } from 'store/confirm';
import {
  CopySurveyModal,
  SurveyContent,
  SurveyParticipants,
  SurveySettings,
} from 'containers/surveys';
import { useGuidedModeParam } from 'utils/useGuidedModeParam';
import { Stack } from '@mui/material';
import { useMe } from 'state/Administrators';

const surveyDetailsTabsSchema = tabsSchema([
  'content',
  'participants',
  'settings',
]);

const SurveyDetailsPage: FC = () => {
  const { t } = useTranslation();
  const predefinedToasts = usePredefinedToasts();
  const { openModal } = useModalContext();
  const { confirmation } = useConfirm();
  const history = useHistory();
  const { hasEditPermission } = useMe();
  const { id: surveyId } = useRouteParams(ROUTES.surveys.survey.details);
  const { value: guided } = useGuidedModeParam();

  const sideTabs = useTabsState(
    [
      {
        key: 'content',
        label: t('benefitTemplates.templateContent'),
      },
      {
        key: 'participants',
        label: t('surveys.participants'),
      },
      {
        key: 'settings',
        label: t('common.settings'),
      },
    ],
    surveyDetailsTabsSchema,
  );

  const { data: survey, isLoading } = useSurvey(surveyId);

  const { mutate: deactivateSurvey, isLoading: isDeactivating } =
    useDeactivateSurvey();
  const { mutate: deleteSurvey, isLoading: isDeleting } = useDeleteSurvey();

  if (isLoading) return <Loader />;
  if (!survey) return <NothingFoundAlert />;

  if (['in_progress', 'ended'].includes(survey.status)) {
    history.push(ROUTES.surveys[survey.type].results.replace(':id', surveyId));
    return null;
  }

  const hasSurveyEditPermission = hasEditPermission([
    getSurveyPermissionName(survey.type),
  ]);

  const onSaveSuccess = () => {
    if (guided) {
      sideTabs.next();
    }
  };

  return (
    <Stack gap={2}>
      <PageHeaderLayout
        breadcrumbs={[
          {
            label: t(`surveys.type.${survey.type}.title`),
            url: ROUTES.surveys[survey.type].list,
          },
          {
            label: survey.name,
          },
        ]}
        side={
          <ActionsMenu
            items={[
              ...(survey.status === 'pending'
                ? [
                    {
                      label: t('surveys.deactivate'),
                      onClick: () => {
                        deactivateSurvey(
                          { surveyId },
                          {
                            onSuccess: () => {
                              predefinedToasts.success.updated();
                            },
                          },
                        );
                      },
                      disabled: isDeactivating || !hasSurveyEditPermission,
                    },
                  ]
                : []),
              {
                label: t('actions.make_copy'),
                disabled: !hasSurveyEditPermission,
                onClick: () => {
                  openModal({
                    component: CopySurveyModal,
                    props: {
                      surveyId,
                      name: survey.name,
                      type: survey.type,
                    },
                  });
                },
              },
              {
                label: t('actions.delete'),
                onClick: async () => {
                  const { confirmed } = await confirmation(
                    t('common.areYouSureToDelete', {
                      title: survey.name,
                    }),
                    undefined,
                    'danger',
                  );

                  if (confirmed) {
                    deleteSurvey(
                      { surveyId },
                      {
                        onSuccess: () => {
                          predefinedToasts.success.deleted();
                          history.push(ROUTES.surveys[survey.type].list);
                        },
                      },
                    );
                  }
                },
                disabled: isDeleting || !hasSurveyEditPermission,
                color: 'error',
              },
            ]}
          />
        }
      />

      <PageContentLayout
        sidebar={
          <SideMenu
            activeTab={sideTabs.activeTab}
            onTabChange={sideTabs.goTo}
            tabs={sideTabs.tabs}
          />
        }
      >
        {sideTabs.activeTab === 'content' && (
          <SurveyContent onSaveSuccess={onSaveSuccess} />
        )}
        {sideTabs.activeTab === 'participants' && (
          <SurveyParticipants onSaveSuccess={onSaveSuccess} />
        )}
        {sideTabs.activeTab === 'settings' && <SurveySettings />}
      </PageContentLayout>
    </Stack>
  );
};

export default SurveyDetailsPage;
